import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button, FormField, InputText } from '@reverse-hr/design-system';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { apiGenerateTokenPost } from '@api/session';

interface TPageLoginForm {
  onSubmit: (email: string) => void;
}

export const PageLoginForm: FC<TPageLoginForm> = ({ onSubmit }) => {
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation('', {
    keyPrefix: 'login.requireLink',
  });

  const isSubmitDisabled = isLoading || !!error;

  const isEmailValid = async (): Promise<boolean> => {
    try {
      await validationSchema.validate({ email });

      setError('');
      return true;
    } catch (_error) {
      setError(t('emailError'));
      return false;
    }
  };

  const onEmailChange = (value: string): void => {
    setEmail(value ?? '');
  };

  const onSubmitClick = async (): Promise<void> => {
    setIsLoading(true);

    const isValid = await isEmailValid();

    if (!isValid) {
      return setIsLoading(false);
    }

    try {
      await apiGenerateTokenPost(email);
      if (onSubmit) {
        onSubmit(email);
      }
    } catch (response: unknown) {
      if (!isErrorResponse(response) || (response.status !== 400 && onSubmit)) {
        return onSubmit(email);
      }

      setError(t('emailError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      isEmailValid();
    } else {
      setError('');
    }
  }, [email]);

  return (
    <>
      <FormField error={error} label={t('emailLabel')}>
        <InputText
          hasErrors={!!error}
          id="login-email"
          name="login-email"
          onChange={onEmailChange}
          placeholder={t('emailPlaceholder') || ''}
          value={email}
        />
      </FormField>

      <div className="c-page-login__submit">
        <Button
          className="c-page-login__cta"
          isDisabled={isSubmitDisabled}
          onClick={onSubmitClick}
        >
          {t('submitCta')}
        </Button>

        <p className="c-page-login__support">
          <Trans i18nKey="login.requireLink.support">
            0<a href="mailto:support@reverse.hr">1</a>
          </Trans>
        </p>
      </div>
    </>
  );
};

const validationSchema = yup.object({
  email: yup.string().email().required(),
});

const isErrorResponse = (error: unknown): error is Response => {
  if (!error || Array.isArray(error) || typeof error !== 'object') {
    return false;
  }

  return 'ok' in error && 'status' in error;
};
